import {
  Grid,
  Tooltip,
  useEventCallback,
  SelectChangeEvent,
  Divider,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { memo } from 'react';
import { PmiButton } from 'shell/components/shared/button';
import PmiTextField from 'shell/components/shared/text-field';
import {
  ForeignVM,
  ItalianVM,
  RegistrationNewPersonVM,
} from 'mutation/view-models/registration-beware';
import InputKeyboard from 'shell/components/shared/input-keyboard';
import ListGender from 'shell/components/shared/gender';
import ChooseDate from 'shell/components/shared/choice-date';
import { DatePickerView } from '@mui/lab/DatePicker/shared';
import { DateTime } from 'luxon';
import Foreign from './foreign';
import Italian from './italian';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import { FormikErrors, FormikTouched } from 'formik';

interface FormNewPersonProps {
  values?: RegistrationNewPersonVM | null;
  setValues: (values: RegistrationNewPersonVM) => void;
  setFieldValue: (
    key: keyof RegistrationNewPersonVM,
    value: RegistrationNewPersonVM[keyof RegistrationNewPersonVM]
  ) => void;
  onSubmit: () => void;
  errors?: FormikErrors<RegistrationNewPersonVM>;
  touched?: FormikTouched<RegistrationNewPersonVM>;
}

const FormNewPersonComponent = ({
  values,
  setValues,
  setFieldValue,
  onSubmit,
  errors,
  touched,
}: FormNewPersonProps): React.ReactElement => {
  const onReset = useEventCallback(() => {
    setValues({});
  });

  const handleGender = useEventCallback((event: SelectChangeEvent<string>) => {
    setFieldValue('gender', event?.target.value);
  });

  const handleChooseDateChange = useEventCallback(
    (date: DateTime | null, choice?: Array<DatePickerView>) => {
      setValues({
        ...(values || {}),
        birthDate: date,
        birthChoiceDate: choice,
      });
    }
  );

  const onChangeRadioGroupBirth = useEventCallback(() => {
    setValues({
      ...(values || {}),
      isForeignBirth: !values?.isForeignBirth,
      italianBirth: undefined,
      foreignBirth: undefined,
    });
  });

  const onChangeForeignBirth = useEventCallback(
    (key: keyof ForeignVM, value: ForeignVM[keyof ForeignVM]) => {
      setFieldValue('foreignBirth', {
        ...(values?.foreignBirth || {}),
        [key]: value,
      });
    }
  );

  const onChangeItalianBirth = useEventCallback(
    (key: keyof ItalianVM, value: ItalianVM[keyof ItalianVM]) => {
      setFieldValue('italianBirth', {
        ...(values?.italianBirth || {}),
        [key]: value,
      });
    }
  );

  const onChangeForeignDomicile = useEventCallback(
    (key: keyof ForeignVM, value: ForeignVM[keyof ForeignVM]) => {
      setFieldValue('foreignDomicile', {
        ...(values?.foreignDomicile || {}),
        [key]: value,
      });
    }
  );

  const onChangeItalianDomicile = useEventCallback(
    (key: keyof ItalianVM, value: ItalianVM[keyof ItalianVM]) => {
      setFieldValue('italianDomicile', {
        ...(values?.italianDomicile || {}),
        [key]: value,
      });
    }
  );

  const onChangeRadioGroupDomicile = useEventCallback(() => {
    setValues({
      ...(values || {}),
      isForeignDomicile: !values?.isForeignDomicile,
      italianDomicile: undefined,
      foreignDomicile: undefined,
    });
  });

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <Tooltip arrow title="Il campo è generato automaticamente">
          <div>
            <PmiTextField
              aria-label="Matricola"
              label="Matricola"
              id="Matricola"
              name="Matricola"
              fullWidth
              value=""
              disabled
            />
          </div>
        </Tooltip>
      </Grid>
      <Grid item xs={12} md={6}>
        <PmiTextField
          label="Codice fiscale"
          aria-label="Codice fiscale"
          name="fiscalCode"
          id="fiscalCode"
          fullWidth
          onChange={(event) => {
            setFieldValue('fiscalCode', event?.target.value);
          }}
          value={values?.fiscalCode?.toUpperCase() || ''}
          inputProps={{ maxLength: '16' }}
          error={touched?.fiscalCode && Boolean(errors?.fiscalCode)}
          helperText={errors?.fiscalCode}
        />
      </Grid>
      <Grid item xs={12} md={9}>
        <InputKeyboard
          label="Cognome"
          id="surname"
          aria-label="Cognome"
          onChange={(value) => {
            setFieldValue('surname', value);
          }}
          value={values?.surname || ''}
          error={errors?.surname}
          touched={touched?.surname}
        />
      </Grid>
      <Grid item xs={12} md={9}>
        <InputKeyboard
          label="Nome"
          id="name"
          aria-label="Nome"
          onChange={(value) => {
            setFieldValue('name', value);
          }}
          value={values?.name || ''}
          error={errors?.name}
          touched={touched?.name}
        />
      </Grid>
      <Grid item xs={12} md={5}>
        <ListGender
          value={values?.gender || ''}
          onChange={handleGender}
          error={touched?.gender && Boolean(errors?.gender)}
        />
      </Grid>
      <Grid item xs={12}>
        <PmiTitleDivider>Dati nascita</PmiTitleDivider>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <ChooseDate
          selected={values?.birthDate || null}
          onChange={handleChooseDateChange}
          choice={values?.birthChoiceDate}
          label="Data nascita"
          error={touched?.birthDate && Boolean(errors?.birthDate)}
          helperText={errors?.birthDate}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="Stato"
            id="isForeignBirth"
            value={values?.isForeignBirth || false}
            onChange={() => onChangeRadioGroupBirth()}
          >
            <FormControlLabel value="false" control={<Radio />} label="Nascita Italiana" />
            <FormControlLabel value="true" control={<Radio />} label="Nascita Estero" />
          </RadioGroup>
        </FormControl>
      </Grid>
      {values?.isForeignBirth ? (
        <Foreign
          values={values?.foreignBirth}
          onChange={onChangeForeignBirth}
          errors={errors?.foreignBirth as FormikErrors<ForeignVM>}
          touched={touched?.foreignBirth as FormikTouched<ForeignVM>}
        />
      ) : (
        <Italian
          values={values?.italianBirth}
          onChange={onChangeItalianBirth}
          errors={errors?.italianBirth as FormikErrors<ItalianVM>}
          touched={touched?.italianBirth as FormikTouched<ItalianVM>}
        />
      )}
      <Grid item xs={12}>
        <PmiTitleDivider>Dati domicilio</PmiTitleDivider>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="Stato"
            id="isForeignDomicile"
            value={values?.isForeignDomicile || false}
            onChange={() => onChangeRadioGroupDomicile()}
          >
            <FormControlLabel value="false" control={<Radio />} label="Italia" />
            <FormControlLabel value="true" control={<Radio />} label="Estero" />
          </RadioGroup>
        </FormControl>
      </Grid>
      {values?.isForeignDomicile ? (
        <Foreign
          values={values?.foreignDomicile}
          onChange={onChangeForeignDomicile}
          errors={errors?.foreignDomicile as FormikErrors<ForeignVM>}
          touched={touched?.foreignDomicile as FormikTouched<ForeignVM>}
        />
      ) : (
        <Italian
          values={values?.italianDomicile}
          onChange={onChangeItalianDomicile}
          errors={errors?.italianDomicile as FormikErrors<ItalianVM>}
          touched={touched?.italianDomicile as FormikTouched<ItalianVM>}
        />
      )}
      <Grid item xs={12}>
        <PmiTextField
          label="Indirizzo"
          aria-label="Indirizzo"
          id="address"
          name="address"
          fullWidth
          onChange={(event) => {
            setFieldValue('address', event?.target.value);
          }}
          value={values?.address || ''}
          error={touched?.address && Boolean(errors?.address)}
          helperText={errors?.address}
        />
      </Grid>
      <Grid item container spacing={2} display="flex" justifyContent="space-between">
        <Grid item xs={12} md={4}>
          <PmiButton
            color="cdmBrandThird"
            variant="outlined"
            fullWidth
            onClick={onReset}
            id="reset"
          >
            Ripulisci
          </PmiButton>
        </Grid>
        <Grid item xs={12} md={4}>
          <PmiButton color="cdmBrandThird" variant="outlined" fullWidth onClick={onSubmit}>
            Inserisci
          </PmiButton>
        </Grid>
      </Grid>
    </Grid>
  );
};

FormNewPersonComponent.displayName = 'Modal new person - form';

const FormNewPerson = memo(FormNewPersonComponent);

export default FormNewPerson;
